import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { APPLICATION_STAGE_REFRESH_QUERY_KEYS } from '@/hooks/useCandidates';
import type {
  GovernedStageOperation,
  LateStageSummary,
} from '@/lib/forkhire/workflow/lateStageWorkflow';

type OperationVariables = {
  applicationId: string;
  sessionId?: string | null;
  operation: GovernedStageOperation;
  idempotencyKey: string;
  reasonCode?: string | null;
  reasonNote?: string | null;
};

type GovernedOperationErrorPayload = {
  error?: string;
  reason?: string;
};

async function readFunctionError(error: unknown): Promise<GovernedOperationErrorPayload | null> {
  if (!error || typeof error !== 'object' || !('context' in error)) return null;
  const context = (error as { context?: { json?: () => Promise<unknown> } }).context;
  if (!context || typeof context.json !== 'function') return null;
  try {
    return await context.json() as GovernedOperationErrorPayload;
  } catch {
    return null;
  }
}

type LateStageSummaryRow = {
  application_id: string;
  has_accepted_offer: boolean;
  has_signed_contract: boolean;
  has_ai_interview_basis: boolean;
  allowed_operations: string[];
  denial_reasons: Record<string, string> | null;
};

export function useLateStageSummaries(applicationIds: string[]) {
  const normalizedIds = [...new Set(applicationIds.filter(Boolean))].sort();
  return useQuery({
    queryKey: ['late-stage-summaries', normalizedIds],
    enabled: normalizedIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        'get_late_stage_application_summaries' as never,
        { _application_ids: normalizedIds } as never,
      );
      if (error) throw error;

      return ((data ?? []) as LateStageSummaryRow[]).reduce<Record<string, LateStageSummary>>(
        (summaries, row) => {
          summaries[row.application_id] = {
            hasAcceptedOffer: row.has_accepted_offer,
            hasSignedContract: row.has_signed_contract,
            hasAiInterviewBasis: row.has_ai_interview_basis,
            allowedOperations: row.allowed_operations ?? [],
            denialReasons: row.denial_reasons ?? {},
          };
          return summaries;
        },
        {},
      );
    },
    staleTime: 15_000,
  });
}

export function useGovernedStageOperation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (variables: OperationVariables) => {
      const { data, error } = await supabase.functions.invoke('govern-stage-operation', {
        body: {
          application_id: variables.applicationId,
          session_id: variables.sessionId ?? null,
          operation: variables.operation,
          idempotency_key: variables.idempotencyKey,
          reason_code: variables.reasonCode ?? variables.operation,
          reason_note: variables.reasonNote ?? null,
        },
      });

      if (error) {
        const payload = await readFunctionError(error);
        const reason = payload?.reason ?? 'operation_rejected';
        throw new Error(t(`hire.lateStage.reasons.${reason}`, {
          defaultValue: payload?.error || t('hire.lateStage.errors.generic'),
        }));
      }

      return data as {
        success: true;
        idempotent: boolean;
        operation_type: GovernedStageOperation;
        stage: string;
      };
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        ...APPLICATION_STAGE_REFRESH_QUERY_KEYS.map((queryKey) =>
          queryClient.invalidateQueries({ queryKey })
        ),
        queryClient.invalidateQueries({ queryKey: ['late-stage-summaries'] }),
        queryClient.invalidateQueries({ queryKey: ['stage-policy-signals', variables.applicationId] }),
        queryClient.invalidateQueries({ queryKey: ['application-history', variables.applicationId] }),
        queryClient.invalidateQueries({ queryKey: ['offers', variables.applicationId] }),
        queryClient.invalidateQueries({ queryKey: ['contract-request', variables.applicationId] }),
        queryClient.invalidateQueries({ queryKey: ['active-interview-sessions'] }),
      ]);

      toast({
        title: t(`hire.lateStage.success.${data.operation_type}`),
      });
    },
    onError: (error) => {
      toast({
        title: t('hire.lateStage.errors.title'),
        description: error instanceof Error ? error.message : t('hire.lateStage.errors.generic'),
        variant: 'destructive',
      });
    },
  });
}

export function createGovernedOperationKey(operation: GovernedStageOperation, applicationId: string) {
  return `forkhire:${operation}:${applicationId}:${crypto.randomUUID()}`;
}
